import type { Duration } from "@js-joda/core";
import {
  ChronoUnit,
  DateTimeFormatter,
  Instant,
  LocalDate,
  LocalDateTime,
  LocalTime,
  ZoneId,
} from "@js-joda/core";
import { EnumDateFormat } from "@/app/services/generated/enums/enumDateFormat.generated";
import { translateWithParams, useI18n } from "@/plugins/i18n";
import type { Tenant } from "@/app/services/generated/tenantService";
import { TenantService } from "@/app/services/generated/tenantService";
import { EnumVolumeUnit } from "@/app/services/generated/enums/enumVolumeUnit.generated";
import { EnumWeightUnit } from "@/app/services/generated/enums/enumWeightUnit.generated";
import type BaseEnumType from "@/app/services/baseEnumType";
import Key from "@/app/types/common/key";
import { dateTimes } from "@/utils/dateTimes";
import type { TpTenant } from "@/app/services/generated/tradeportalService";
import type { Maybe } from "@/components/utils/typeHelpers";
import { makeRef } from "@/components/composables/reactivityTypes";
import type { ShallowRef } from "vue";

const { $t } = useI18n();
interface DateRangeFromArray {
  fromDate: LocalDate | null;
  toDate: LocalDate | null;
}

export interface UnitDisplayPreference<T> {
  unit: T;
  conversionMultiplierFromBaseUnit: number;
  singularName: string;
  suffix: string;
  maxDecimalPlaces: number;
}

export const hlUnitPreference: UnitDisplayPreference<EnumVolumeUnit> = {
  unit: EnumVolumeUnit.metricHectolitre,
  conversionMultiplierFromBaseUnit: 0.01,
  singularName: "hectolitre",
  suffix: "HL",
  maxDecimalPlaces: 5,
} as const;

export type WeightUnitPreference =
  | "general"
  | UnitDisplayPreference<EnumWeightUnit>;

export type VolumeUnitPreference =
  | "general"
  | "vessels"
  | UnitDisplayPreference<EnumVolumeUnit>;

export function isUnitDisplayPreference<
  T extends WeightUnitPreference | VolumeUnitPreference,
>(preference: T): preference is T {
  return (
    preference !== "general" &&
    preference !== "vessels" &&
    preference.conversionMultiplierFromBaseUnit !== undefined
  );
}

export function resolveVolumeUnitPreference(
  preference: VolumeUnitPreference
): UnitDisplayPreference<EnumVolumeUnit> {
  if (preference === "general") {
    return tenantService.settings.displayUnitPreferences.generalVolumes;
  }

  if (preference === "vessels") {
    return tenantService.settings.displayUnitPreferences.vesselVolumes;
  }

  return preference;
}

export function resolveWeightUnitPreference(
  preference: WeightUnitPreference
): UnitDisplayPreference<EnumWeightUnit> {
  if (preference === "general") {
    return tenantService.settings.displayUnitPreferences.generalWeights;
  }

  return preference;
}

export interface TenantSettings {
  currency: {
    currencyCode: string; // ISO 4217
    symbolPrefix: string;
    symbolSuffix: string;
    minDecimalPlaces: number;
    maxDecimalPlaces: number;

    quantisedMaxDecimalPlaces: number;
  };
  location: {
    countryCode: string; // ISO 8061
    timezoneName: string; // IANA Timezone 2019a
  };
  dateFormat: EnumDateFormat;
  thousandsSeparator: string;
  timeFormat: string;
  measureMinDecimalPlaces: number;
  measureMaxDecimalPlaces: number;
  displayUnitPreferences: {
    generalVolumes: UnitDisplayPreference<EnumVolumeUnit>;
    vesselVolumes: UnitDisplayPreference<EnumVolumeUnit>;
    generalWeights: UnitDisplayPreference<EnumWeightUnit>;
  };
}

function getDefaultTenantSettings(): TenantSettings {
  return {
    currency: {
      currencyCode: "GBP",
      symbolPrefix: "£",
      symbolSuffix: "",
      minDecimalPlaces: 2,
      maxDecimalPlaces: 5,
      quantisedMaxDecimalPlaces: 2,
    },
    location: {
      countryCode: "GB",
      timezoneName: "Europe/London",
    },
    dateFormat: EnumDateFormat.dDMMYYYY,
    timeFormat: "HH:mm",
    measureMinDecimalPlaces: 2,
    measureMaxDecimalPlaces: 5,
    thousandsSeparator: "",
    displayUnitPreferences: {
      generalVolumes: {
        unit: EnumVolumeUnit.metricLitre,
        conversionMultiplierFromBaseUnit: 1,
        singularName: "litre",
        suffix: "L",
        maxDecimalPlaces: 5,
      },
      vesselVolumes: {
        unit: EnumVolumeUnit.metricLitre,
        conversionMultiplierFromBaseUnit: 1,
        singularName: "litre",
        suffix: "l",
        maxDecimalPlaces: 5,
      },
      generalWeights: {
        unit: EnumWeightUnit.metricKilogram,
        conversionMultiplierFromBaseUnit: 1,
        singularName: "kilogram",
        suffix: "kg",
        maxDecimalPlaces: 5,
      },
    },
  };
}

function displayUnitPreferencesFromTenant<T extends BaseEnumType>(
  unit: T,
  conversionMultiplierFromBaseUnit: number,
  maxDecimalPlaces: number
): UnitDisplayPreference<T> {
  return {
    unit: unit,
    conversionMultiplierFromBaseUnit,
    maxDecimalPlaces,
    singularName: $t(
      Key.fromString(unit.translationKey + "_singular_name")
    ).toString(),
    suffix: $t(Key.fromString(unit.translationKey + "_suffix")).toString(),
  };
}

export default class TenantServiceExtended extends TenantService {
  /**
   * Derived display/formatting context for the current tenant session.
   * Replaced wholesale on tenant setup (not mutated field-by-field at runtime).
   */
  private readonly _settings: ShallowRef<TenantSettings> = makeRef(
    getDefaultTenantSettings()
  );

  get settings(): TenantSettings {
    return this._settings.value;
  }

  public configureForTpTenant(tenant: TpTenant | null) {
    const newSettings = getDefaultTenantSettings();
    if (tenant) {
      // NB: Server uses maxdecimalplaces when quantising to (e.g.) pennies
      newSettings.currency.quantisedMaxDecimalPlaces =
        tenant.currencyMaxDecimalPlaces;
      newSettings.currency.symbolPrefix = tenant.currencySymbolPrefix;
      newSettings.currency.symbolSuffix = tenant.currencySymbolSuffix;
      newSettings.dateFormat = tenant.dateFormat;
      newSettings.timeFormat = tenant.timeFormat;
      newSettings.location.timezoneName = tenant.timezone;
    }
    this._settings.value = newSettings;
  }

  public tenantChanged(tenant: Tenant | null) {
    const newSettings = getDefaultTenantSettings();
    if (tenant) {
      newSettings.currency.currencyCode =
        tenant.currencyConfiguration.currencyCode;
      // NB: Server uses maxdecimalplaces when quantising to (e.g.) pennies
      newSettings.currency.quantisedMaxDecimalPlaces =
        tenant.currencyConfiguration.currencyMaxDecimalPlaces;
      newSettings.currency.symbolPrefix =
        tenant.currencyConfiguration.currencySymbolPrefix;
      newSettings.currency.symbolSuffix =
        tenant.currencyConfiguration.currencySymbolSuffix;
      newSettings.dateFormat = tenant.dateAndTimeConfiguration.dateFormat;
      newSettings.timeFormat = tenant.dateAndTimeConfiguration.timeFormat;
      newSettings.thousandsSeparator =
        tenant.numberConfiguration.thousandsSeparator;
      newSettings.location.timezoneName =
        tenant.dateAndTimeConfiguration.timezoneName;

      newSettings.displayUnitPreferences.generalVolumes =
        displayUnitPreferencesFromTenant(
          tenant.displayUnitPreferences.generalVolumes,
          tenant.displayUnitPreferences.readOnly
            .generalVolumesConversionMultiplierFromLitre,
          tenant.displayUnitPreferences.readOnly.generalVolumesDisplayDps
        );

      newSettings.displayUnitPreferences.vesselVolumes =
        displayUnitPreferencesFromTenant(
          tenant.displayUnitPreferences.vesselVolumes,
          tenant.displayUnitPreferences.readOnly
            .vesselVolumesConversionMultiplierFromLitre,
          tenant.displayUnitPreferences.readOnly.vesselVolumesDisplayDps
        );

      newSettings.displayUnitPreferences.generalWeights =
        displayUnitPreferencesFromTenant(
          tenant.displayUnitPreferences.generalWeights,
          tenant.displayUnitPreferences.readOnly
            .generalWeightsConversionMultiplierFromKilogram,
          tenant.displayUnitPreferences.readOnly.generalWeightsDisplayDps
        );
    }
    this._settings.value = newSettings;
  }

  /**
   * Rounds a number (Half Up) to the correct precision.
   */
  public roundNumber(
    toRound: number,
    maxDecimalPlaces = this.settings.measureMaxDecimalPlaces
  ) {
    return +toRound.toFixed(maxDecimalPlaces);
  }

  /**
   * Formats a number for display with min and max decimal places.
   */
  public formatNumber(
    value: number,
    minDecimalPlaces = this.settings.measureMinDecimalPlaces,
    maxDecimalPlaces = this.settings.measureMaxDecimalPlaces
  ): string {
    const thousandsSeparator = this.settings.thousandsSeparator;
    const formatter = Intl.NumberFormat(undefined, {
      minimumFractionDigits: minDecimalPlaces,
      maximumFractionDigits: maxDecimalPlaces,
      useGrouping: !!thousandsSeparator,
    });
    // If grouping then we need to manually control the grouping separator as Intl.NumberFormat does not
    // allow us to control it independantly
    if (this.settings.thousandsSeparator) {
      const parts = formatter.formatToParts(value);
      return parts
        .map((p) => (p.type === "group" ? thousandsSeparator : p.value))
        .join("");
    }
    return formatter.format(value);
  }

  /**
   * Rounds a currency to the default system precision for currencies.
   */
  public roundCurrency(
    toRound: number,
    quantise: boolean = false,
    maxDecimalPlaces = quantise
      ? this.settings.currency.quantisedMaxDecimalPlaces
      : this.settings.currency.maxDecimalPlaces
  ) {
    return this.roundNumber(toRound, maxDecimalPlaces);
  }

  /**
   * Formats a currency for display. Will use tenant currency settings to quantise the value if desired.
   */
  public formatCurrency(
    currencyValue: number,
    quantise: boolean = false,
    minDecimalPlaces = this.settings.currency.minDecimalPlaces,
    maxDecimalPlaces = quantise
      ? this.settings.currency.quantisedMaxDecimalPlaces
      : this.settings.currency.maxDecimalPlaces
  ): string {
    return `${currencyValue < 0 ? "-" : ""}${
      this.settings.currency.symbolPrefix
    }${this.formatNumber(
      Math.abs(currencyValue),
      minDecimalPlaces,
      maxDecimalPlaces
    )}${this.settings.currency.symbolSuffix}`.trim();
  }

  public toDisplayPreferenceVolume(
    litreValue: number,
    context: VolumeUnitPreference,
    maxDecimalPlaces?: number
  ): number {
    const unitDisplayPreference = resolveVolumeUnitPreference(context);

    return this.roundNumber(
      litreValue * unitDisplayPreference.conversionMultiplierFromBaseUnit,
      maxDecimalPlaces ?? unitDisplayPreference.maxDecimalPlaces
    );
  }

  public formatVolume(litreValue: number, context: VolumeUnitPreference) {
    const unitDisplayPreference = resolveVolumeUnitPreference(context);

    return `${this.toDisplayPreferenceVolume(litreValue, context)} ${
      unitDisplayPreference.suffix
    }`;
  }

  public toDisplayPreferenceWeight(
    kgValue: number,
    maxDecimalPlaces?: number,
    context?: WeightUnitPreference
  ): number {
    const unitDisplayPreference = resolveWeightUnitPreference(
      context ?? tenantService.settings.displayUnitPreferences.generalWeights
    );

    return this.roundNumber(
      kgValue * unitDisplayPreference.conversionMultiplierFromBaseUnit,
      maxDecimalPlaces ?? unitDisplayPreference.maxDecimalPlaces
    );
  }

  public formatWeight(kgValue: number, context?: WeightUnitPreference) {
    const unitDisplayPreference = resolveWeightUnitPreference(
      context ?? tenantService.settings.displayUnitPreferences.generalWeights
    );

    return `${this.toDisplayPreferenceWeight(kgValue)} ${
      unitDisplayPreference.suffix
    }`;
  }

  public getTimezone(): ZoneId {
    return ZoneId.of(this.settings.location.timezoneName);
  }

  /**
   * Get a LocalDate representing today for the current tenant.
   */
  get today(): LocalDate {
    return LocalDate.now(ZoneId.of(this.settings.location.timezoneName));
  }

  get nowDateTime(): LocalDateTime {
    return LocalDateTime.now(tenantService.getTimezone());
  }

  get now(): Instant {
    return Instant.now();
  }

  /**
   * Formats a LocalDate into a string using the tenants preferences.
   */
  public formatDate(date: LocalDate): string {
    return dateTimes.formatDate(date, this.settings.dateFormat);
  }

  /**
   * Formats a LocalTime into a string using the tenants preferences.
   */
  public formatTime(time: LocalTime): string {
    return DateTimeFormatter.ofPattern(this.settings.timeFormat).format(time);
  }

  /*
   * Formats an Instant into a string of Date + Time using tenants preferences
   */
  public formatDateTime(instant: Maybe<Instant>): string {
    const dateTime = this.instantToDateTime(instant);
    return dateTime
      ? `${this.formatDate(dateTime.toLocalDate())} ${this.formatTime(dateTime.toLocalTime())}`
      : "";
  }

  /**
   * Parses a date in the tenant's date format to a LocalDate
   */
  public parseDate(date: string): LocalDate {
    return LocalDate.parse(date, dateTimes.formatter(this.settings.dateFormat));
  }

  /**
   * Parses a time in the tenant's time format to a LocalTime
   */
  public parseTime(date: string): LocalTime {
    return LocalTime.parse(
      date,
      DateTimeFormatter.ofPattern(this.settings.timeFormat)
    );
  }

  public instantToDate(instant: Instant): LocalDate;
  public instantToDate(instant: Maybe<Instant>): LocalDate | null;
  public instantToDate(instant: Maybe<Instant>): LocalDate | null {
    return instant
      ? LocalDate.ofInstant(
          instant,
          ZoneId.of(this.settings.location.timezoneName)
        )
      : null;
  }

  public instantToTime(instant: Instant): LocalTime {
    return LocalTime.ofInstant(
      instant,
      ZoneId.of(this.settings.location.timezoneName)
    );
  }

  public instantToDateTime(instant: Instant): LocalDateTime;
  public instantToDateTime(instant: Maybe<Instant>): LocalDateTime | null;
  public instantToDateTime(instant: Maybe<Instant>): LocalDateTime | null {
    return instant
      ? LocalDateTime.ofInstant(
          instant,
          ZoneId.of(this.settings.location.timezoneName)
        )
      : null;
  }

  // Converts a given date/time to a zonedDateTime, then an instant.
  public dateTimeToInstant(
    localDate: LocalDate,
    localTime: LocalTime
  ): Instant {
    const localDateTime = LocalDateTime.of(localDate, localTime);
    return localDateTime.atZone(tenantService.getTimezone()).toInstant();
  }

  public startOfDay(date: LocalDate) {
    return this.dateTimeToInstant(date, LocalTime.MIDNIGHT);
  }

  public endOfDay(date: LocalDate) {
    return this.dateTimeToInstant(date, LocalTime.MAX);
  }

  /**
   * Should be used inconjunction with the resopnses from BmDateRange
   * @param dates The dates provided by the BmDateRange component
   */
  public getDateRangeFromArray(
    dates: readonly LocalDate[]
  ): DateRangeFromArray {
    return {
      fromDate: dates.length ? dates[0] : null,
      // toDate should be fromDate if it exists with no toDate to create the boundary
      toDate: dates.length > 1 ? dates[1] : dates[0] || null,
    };
  }

  /**
   * Formats a Duration into hours and minutes
   */
  public formatDuration(duration: Duration): string {
    const time = LocalTime.ofSecondOfDay(duration.seconds());
    const hours = time.hour();
    const minutes = time.minute();
    return translateWithParams("distribution_duration_format", {
      hours: hours.toString(),
      minutes: minutes.toString(),
    }).toString();
  }

  /**
   * Parses a string into a formatted LocalTime
   */
  public parseFormattedTime(input: string | null): LocalTime | null {
    enum TimeType {
      AM,
      PM,
      MILITARY,
    }

    if (input) {
      let internalInput: string = input.trim();

      // determine if input is AM/PM/MILITARY
      let timeType: TimeType = TimeType.MILITARY;
      if (internalInput.length > 2) {
        const lastTwoCharacters = internalInput.substr(
          internalInput.length - 2,
          internalInput.length
        );
        switch (lastTwoCharacters.toLowerCase()) {
          case "am": {
            timeType = TimeType.AM;
            break;
          }
          case "pm": {
            timeType = TimeType.PM;
            break;
          }
          default: {
            timeType = TimeType.MILITARY;
          }
        }
      }

      internalInput = input.replace(/[^0-9]/g, "");

      if (!internalInput || internalInput.length > 4) {
        return null;
      }

      let minutes = "00";
      let hours = "00";

      // determine which characters of internalInput represent hours/minutes
      if (internalInput.length <= 2) {
        hours = internalInput;
      } else if (internalInput.length === 3) {
        hours = internalInput[0];
        minutes = internalInput.substr(
          internalInput.length - 2,
          internalInput.length
        );
      } else {
        hours = internalInput.substr(0, internalInput.length - 2);
        minutes = internalInput.substr(
          internalInput.length - 2,
          internalInput.length
        );
      }

      // reject invalid hours/minutes values
      if (+hours >= 24 || +minutes >= 60) {
        return null;
      }

      // add 12 hours if time is PM
      if (timeType === TimeType.PM && +hours < 12) {
        hours = (+hours + 12).toString();
      }

      // handle 12am input
      if (timeType === TimeType.AM && hours === "12") {
        hours = "00";
      }

      if (hours.length === 1) {
        hours = "0" + hours;
      }

      return this.parseTime(hours + ":" + minutes);
    }
    return null;
  }

  /**
   * Returns the time between two instants, formatted as:
   *
   * "updated 5 minutes ago"
   *
   * "updated 3 hours ago"
   *
   * "updated 2 days ago"
   */
  public formatTimeBetweenInstants(
    instantA: Instant,
    instantB: Instant
  ): string {
    const minutesBetweenInstants = ChronoUnit.MINUTES.between(
      instantA,
      instantB
    );

    if (minutesBetweenInstants < 60) {
      return translateWithParams("batch_performance_updated_minutes_ago", {
        minutes: minutesBetweenInstants.toString(),
      }).toString();
    }

    if (minutesBetweenInstants >= 60 && minutesBetweenInstants < 60 * 24) {
      return translateWithParams("batch_performance_updated_hours_ago", {
        hours: (minutesBetweenInstants / 60).toFixed(0).toString(),
      }).toString();
    }

    return translateWithParams("batch_performance_updated_days_ago", {
      days: (minutesBetweenInstants / 60 / 24).toFixed(0).toString(),
    }).toString();
  }

  public isTodayInRangeInclusive(from: LocalDate, to: LocalDate) {
    return !this.today.isBefore(from) && !this.today.isAfter(to);
  }

  public readonly supportedCurrencyCodes = [
    "GBP",
    "EUR",
    "KES",
    "USD",
    "SEK",
    "ZAR",
    "AUD",
    "NZD",
    "COP",
    "THB",
    "PHP",
    "XCD",
  ];

  public milesToMetres(miles: number): number {
    return miles * 1609;
  }

  public metresToMiles(metres: number): number {
    return metres / 1609;
  }
}

export const tenantService = new TenantServiceExtended();
